import ListPopOut from '../ListPopOut/ListPopOut'
import OverflowPlaceholder from './OverflowPlaceholder/OverflowPlaceholder'
import WrappedChildren from './WrappedChildren'
import classes from './ComponentOverflow.module.scss'
import {
  MAX_OVERFLOW_PLACEHOLDER_SIZE,
  OFFSET_SIZE
} from './constants'
import {
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState
} from 'react'
import { getTotalChildrenWidth } from './getTotalChildrenWidth'
import type { PopperProps } from 'react-popper'
import type { ReactNode } from 'react'

export interface BaseProps {
  children?: ReactNode | ReactNode[]
  overflowTooltipsHeader?: ReactNode
  extraList?: ReactNode
  extraListPlacement?: PopperProps['placement']
  columnGap?: number
  isChildrenHasTextOverflow?: boolean
  width?: number
  isDisabled?: boolean
  isPopOutAlign?: boolean
  calculateByTextWidth?: boolean
  hasPlaceholderWhenNoOverflow?: boolean
  minOverflowWidth?: number
  overflowPopOutHasFixWidth?: boolean
  overflowPopOutLabel?: string
  overflowLabelReplacement?: ReactNode
  maxContainerWidth?: number
  isAlignItem?: boolean
}

export interface WithExtraList {
  extraList?: ReactNode
  extraListPlacement?: PopperProps['placement']
  isAlignItem?: boolean
}

export interface WithoutExtraList {
  extraList?: never
  extraListPlacement?: never
  isAlignItem?: never
}

type Props = BaseProps & (WithExtraList | WithoutExtraList)

const getTextWidth = (text: string) => {
  const canvasElement = document.createElement('canvas')
  const context = canvasElement.getContext('2d')

  if (context) {
  // eslint-disable-next-line fp/no-mutation
    context.font = '13px Open Sans'
  }

  return context?.measureText(text).width || 0
}

// eslint-disable-next-line complexity
const ComponentOverflow = ({
  children,
  overflowTooltipsHeader,
  extraList,
  extraListPlacement,
  columnGap = 4,
  isChildrenHasTextOverflow = false,
  width = 400,
  isDisabled = false,
  isPopOutAlign = false,
  calculateByTextWidth = false,
  hasPlaceholderWhenNoOverflow = true,
  minOverflowWidth = 20, // this is to prevent children just render "..." without other chars
  overflowPopOutHasFixWidth = true,
  overflowPopOutLabel = 'Summary',
  overflowLabelReplacement,
  maxContainerWidth,
  isAlignItem = true
}: Props) => {
  const containerRef = useRef<HTMLDivElement|null>(null)
  const childrenRef = useRef<HTMLElement[]>([])
  const [totalRenderItem, setTotalRenderItem] = useState(0)
  const [lastChildMaxWdith, setLastChildMaxWidth] = useState(0)

  useEffect(() => {
    if (containerRef.current === null) return

    // the container width need to handle ramainding not render-able count text
    const containerWidth = maxContainerWidth || width - (columnGap + MAX_OVERFLOW_PLACEHOLDER_SIZE)

    // eslint-disable-next-line complexity
    const renderableComponent = childrenRef.current.reduce<HTMLElement[]>((totalRef, childRef, childIndex) => {
      // force return totalRef if hit limitWidth for component
      const totalChildRefWidth = childrenRef.current.slice(0, childIndex)

      const lastChilderen = totalChildRefWidth.map(child => child.lastChild)
      const componentWidth = getTotalChildrenWidth(totalChildRefWidth.map(child => child.offsetWidth), columnGap)

      const textwidth = getTotalChildrenWidth(lastChilderen.map(child => getTextWidth((child as HTMLSpanElement).innerText) + 12 + 4 + OFFSET_SIZE), columnGap) // 12 is hard code padding left and right, 4px is border px

      const limitWidth = calculateByTextWidth
        ? textwidth
        : componentWidth

      if (limitWidth > containerWidth) return totalRef

      const newRefs = [...totalRef, childRef]
      const newRefWidths = newRefs.map(newRef => newRef.offsetWidth)
      const totalWidth = getTotalChildrenWidth(newRefWidths, columnGap)

      if (totalWidth <= containerWidth) return newRefs

      const overflowWidth = totalWidth - (containerWidth + columnGap)
      const remaindingWidthCanRender = newRefWidths[newRefs.length - 1] - overflowWidth

      if (remaindingWidthCanRender > minOverflowWidth && totalRef.length === childIndex && isChildrenHasTextOverflow) {
        setLastChildMaxWidth(remaindingWidthCanRender)

        return newRefs
      }

      return totalRef
    }, [])

    setTotalRenderItem(renderableComponent.length)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childrenRef])

  const renderableComponents = () => {
    if (Array.isArray(children)) {
      return children.filter((item, index) => item && index < totalRenderItem).map((item, index) =>
        <WrappedChildren
          key={index}
          maxWidth={
            index === children.filter((item, index) => item && index < totalRenderItem).length - 1
              ? lastChildMaxWdith || undefined
              : undefined
          }
        >
          {item}
        </WrappedChildren>
      )
    }

    return undefined
  }

  const getDisplayableContents = () => Array.isArray(children)
    ? renderableComponents()
    : (
      <WrappedChildren>
        {children}
      </WrappedChildren>
    )

  const assignChildrenRef = () => Array.isArray(children)
    ? children.map((child, index) =>
      <WrappedChildren
        key={index}
        ref={childRef => {
          // eslint-disable-next-line fp/no-mutation
          childrenRef.current[index] = childRef as HTMLElement
        }}
      >
        {child}
      </WrappedChildren>
    )
    : (
      // eslint-disable-next-line fp/no-mutation
      <WrappedChildren ref={childRef => { childrenRef.current[0] = childRef as HTMLElement }}>
        {children}
      </WrappedChildren>
    )

  const hasOverflow = Array.isArray(children) && !!totalRenderItem && totalRenderItem !== children.length

  const renderDefaultOverflowPlaceholder = hasOverflow &&
    (
      <OverflowPlaceholder
        gap={columnGap}
        placeholderHeader={overflowTooltipsHeader}
      >
        {
          children.filter((_child, index) => index >= totalRenderItem)
        }
      </OverflowPlaceholder>
    )

  const renderOverflowLabelReplacement = hasOverflow &&
    isValidElement(overflowLabelReplacement) &&
    cloneElement(overflowLabelReplacement, {}, children.filter((_child, index) => index >= totalRenderItem))

  const renderOverflow = overflowLabelReplacement
    ? renderOverflowLabelReplacement
    : renderDefaultOverflowPlaceholder

  const isAlignItemStyle = !isAlignItem
    ? classes.NoAlign
    : ''

  const componentOverflowClasses = [
    classes.ComponentOverflow,
    isAlignItemStyle
  ].filter(classes => classes).join(' ')

  return (
    <div
      className={componentOverflowClasses}
      ref={containerRef}
      style={{ columnGap: `${columnGap}px` }}
    >
      {
        totalRenderItem
          ? getDisplayableContents()
          : assignChildrenRef()
      }
      {
        extraList && (hasOverflow || hasPlaceholderWhenNoOverflow)
          ? (
            <ListPopOut
              counter={
                hasOverflow
                  ? children.filter((_child, index) => index >= totalRenderItem).length
                  : undefined
              }
              hasFixWidth={overflowPopOutHasFixWidth}
              isDisabled={isDisabled}
              isPopOutAlign={isPopOutAlign}
              label={overflowPopOutLabel}
              placement={extraListPlacement}
              title={overflowPopOutLabel}
            >
              {extraList}
            </ListPopOut>
          )
          : renderOverflow
      }
    </div>
  )
}

export default ComponentOverflow
